import { Controller } from 'stimulus';
import { templateContent } from '@/js/util';

const MANUAL_REMOVAL_TEMPLATE_ID = 'manual-removal-modal-template';

export default class extends Controller {
  static targets = ['selector'];
  static classes = ['active', 'inactive'];

  // Click handler shared by every crop option button. The shape is read from
  // the clicked button's `data-shape`. `line` is the manual (pen tool) removal
  // rather than a crop shape, so it closes the panel and opens the manual-removal modal
  select({ currentTarget: { dataset } }) {
    if (dataset.shape === 'line') {
      this.cancel();
      this.#openManualRemoval();
      return;
    }

    this.#selectShape(dataset.shape);
  }

  // Apply/cancel are owned by the legacy crop engine (gui-crop.js), so we just
  // signal it. The panel's dropdown closes off the same events (see the view).
  apply() {
    this.dispatch('apply', { prefix: 'crop', target: document });
  }

  cancel() {
    this.dispatch('cancel', { prefix: 'crop', target: document });
  }

  // Dispatched by the legacy crop engine (gui-crop.js) when the crop tool
  // opens, so the panel reflects the item being cropped.
  initShape({ detail: { shape, kind } }) {
    this.#toggleManualRemoval(kind !== 'swatch');
    this.#selectShape(shape);
  }

  // The line button is manual (pen-tool) removal, which doesn't apply to a
  // solid-color swatch, so we hide it when cropping one. We set `display`
  // inline rather than toggling the `hidden` utility as just setting hidden
  // gets overridden by some other tailwind utility
  #toggleManualRemoval(visible) {
    const line = this.selectorTargets.find((selector) => selector.dataset.shape === 'line');

    if (line) {
      line.style.display = visible ? '' : 'none';
    }
  }

  #openManualRemoval() {
    if (!document.querySelector('.item.selected')?.sourceImage) {
      return;
    }

    document.body.append(templateContent(document.getElementById(MANUAL_REMOVAL_TEMPLATE_ID)));
  }

  #selectShape(shape) {
    this.selectorTargets.forEach((selector) => {
      this.#setState(selector, selector.dataset.shape === shape);
    });

    this.#applyOverlayShape(shape === 'circle');
  }

  #setState(element, active) {
    this.activeClasses.forEach((activeClass) => element.classList.toggle(activeClass, active));
    this.inactiveClasses.forEach((inactiveClass) =>
      element.classList.toggle(inactiveClass, !active)
    );
    element.setAttribute('aria-pressed', String(active));
  }

  // The crop overlay lives in a separate template (boards/_crop_overlay), so we
  // reach it by its stable functional selectors rather than a Stimulus target.
  #applyOverlayShape(isCircle) {
    const overlay = document.querySelector('.gui-crop__cropped-overlay');
    const image = document.querySelector('.gui-crop__image');

    overlay?.classList.toggle('gui-crop__cropped-overlay--circle', isCircle);
    overlay?.classList.toggle('rounded-[100%]', isCircle);
    image?.classList.toggle('gui-crop__image--circle', isCircle);
  }
}
